import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Button, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Para = ({ children }) => (
  <Typography sx={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.85, mb: 1.5 }}>
    {children}
  </Typography>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc', py: 5, fontFamily: "'Inter', sans-serif" }}>
      <Container maxWidth="md">
        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/login')}
          sx={{ mb: 3, textTransform: 'none', color: '#6366f1', fontWeight: 600, '&:hover': { background: 'rgba(99,102,241,0.06)' } }}
        >
          Back to Login
        </Button>

        {/* Card */}
        <Box
          sx={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
            p: { xs: 3, md: 5 },
            border: '1px solid #e8ecff',
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: '#0f172a', mb: 0.5 }}>
              Privacy Policy
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              PayNTric Private Limited · Last Updated: 2026
            </Typography>
          </Box>

          <Divider sx={{ mb: 4, borderColor: '#e8ecff' }} />

          <Para>
            We <strong>PayNTric Private Limited</strong> take every reasonable step to ensure that your personal information that we process is accurate and, where necessary, kept up to date, and any of your personal information that we process that you inform us is inaccurate (having regard to the purposes for which they are processed) is erased or rectified. You may access, correct, and update your personal information directly through the functionalities provided on the Platform. You may delete certain non-mandatory information by logging into our website and visiting Profile and Settings sections. You can also write to us at the contact information provided below to assist you with these requests.
          </Para>

          <Para>
            You have an option to withdraw your consent that you have already provided by writing to us at the contact information provided below. Please mention “for withdrawal of consent” in the subject line of your communication. We will verify such requests before acting upon your request. Please note, however, that withdrawal of consent will not be retroactive and will be in accordance with the terms of this Privacy Policy, related Terms of Use and applicable laws. In the event you withdraw consent given to us under this Privacy Policy, such withdrawal may hamper your access to the Platform or restrict provision of our services to you for which we consider that information to be necessary.
          </Para>

          <Para>
            The Information Technology Act of 2000 and the rules it formed, as well as the revised provisions in different legislation relating to electronic documents and records as a result of the Information Technology Act 2000, have created this Privacy Policy as an electronic record in the form of an electronic contract. There is no need for a written, digital, or electronic signature on this privacy policy between you and PayNTric Private Limited. (both terms defined below), this Privacy Policy is a binding contract.
          </Para>

          <Section title="User Information">
            <Para>
              You must register in order to utilize some services on our website and mobile application. This information includes a) your name; b) your email address; c) your sex; d) your age; e) your PIN code; j) your password; and/or f) your occupation, interests, and similar information. We are able to make improvements to our sites and give you the best user-friendly experience with mobile applications also. Thanks to the information provided by you.
            </Para>
            <Para>
              All necessary information is service-specific, and we reserve the right to use the aforementioned user information to operate, manage, improve, and safeguard our services, including our advertising services, as well as to create new services. If the material is publicly accessible in the public domain, provided in accordance with the Right to Material Act of 2005 or any other currently in effect law, it will not be regarded as sensitive.
            </Para>
          </Section>

          <Section title="Cookies">
            <Para>
              We may use “cookies” or other similar electronic instruments to gather data in order to provide each visitor a special, random number as a User Identification (User ID) and better understand their unique interests using the Identified Computer. This will improve the responsiveness of the site & mobile application for our users. Even if we set a cookie on your computer, we won’t be able to identify you until you willingly do so (by signing up, for instance). Only data you provide—for instance, when you request to associate with us —can be used to identify you personally in cookies. Cookies are unable to read data from your hard drive. If you click on one of our advertisers’ adverts, your browser might also receive one of their own cookies, a procedure that we have no control over.
            </Para>
            <Para>
              When you visit our website, our web servers automatically record a few details about your computer’s Internet connection, including your IP address (Your IP address is a number that helps Internet-connected devices know to relay data, such as the web pages you view, to you). You cannot be personally identified by your IP address. We use this information to fulfill your requests for our web pages, to better understand the interests of our users, to monitor site traffic, and to share with advertising the regions from which our visitors originate on our website or mobile application.
            </Para>
          </Section>

          <Section title="Link to the other sites">
            <Para>
              Our policy discloses the privacy practices for our own website & mobile application only. Our website & mobile application provides links to other websites also that are beyond our control. You agree not to hold us liable or accountable in any manner for how you use these sites.
            </Para>
          </Section>

          <Section title="Information Sharing">
            <Para>
              In the following specific instances, we may disclose sensitive personal information to any third party without first getting the user’s consent:
            </Para>
            <Para>
              (a) When disclosure is requested or mandated by law, a court, a governmental agency, or other authority, in order to verify identify, prevent, detect, or investigate an incident—including one using computers—or to prosecute and punish offenders. These disclosures are disclosed in the good faith belief that they are reasonably required for upholding the terms of this Agreement and for compliance with all relevant laws and regulations.
            </Para>
            <Para>
              (b) In order to process personal information on our behalf, we propose to communicate this information with our group companies and the officers and staff of those organizations. We also make sure that the individuals who receive this information consent to its processing in accordance with our instructions, this Privacy Statement, and any other applicable confidentiality and security measures.
            </Para>
          </Section>

          <Section title="Information Security">
            <Para>
              We put the required security measures in place to prevent unauthorized access to, as well as unauthorized data change, disclosure, or destruction. To prevent unauthorized access to the systems where we store personal data, security measures such as appropriate physical security and encryption are used, as well as internal audits of our data collection, storage, and processing practices.
            </Para>
            <Para>
              All information gathered through our website and mobile application is securely stored in our controlled database. The database is maintained on firewall-protected servers with password-protected access. No system is impenetrable, notwithstanding the efficiency of our security measures. Neither the security of our database nor the safety of the data you provide when sending it to us over the Internet can be guaranteed by us. Of course, anyone with Internet access can access whatever information you submit in a posting to a discussion forum.
            </Para>
            <Para>
              The internet, however, is a medium that constantly changes. We could occasionally update our privacy statement to reflect upcoming changes that will be required. Naturally, regardless of what the new policy may be, we will always utilize any information we acquire in accordance with the policy under which it was collected.
            </Para>
          </Section>

          <Section title="Grievance Redressal">
            <Para>
              <strong>Redressal Mechanism:</strong> Any complaints, abuse, or concerns regarding material, comments, or violations of these terms should be reported right away to the authorized grievance officer, who is listed below. The complaint should be made in writing or by email that is signed with an electronic signature and sent to support@jcentrix.com from 9:30 AM to 6:30 PM to help resolve your query.
            </Para>
            <Para>
              <strong>Level 2:</strong> If you are not satisfied with the Level 1 resolution, please write to Nodal Officer, Customer Grievance, MR. Yogesh at <a href="mailto:yogesh@jcentrix.com" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>yogesh@jcentrix.com</a>
            </Para>
          </Section>

          <Divider sx={{ my: 4, borderColor: '#e8ecff' }} />

          {/* Contact Details Box */}
          <Box
            sx={{
              background: '#fafbff',
              border: '1px solid #e8ecff',
              borderRadius: '12px',
              p: 3,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', mb: 1.5 }}>
              Contact Information
            </Typography>
            {[
              ['Managed By', 'PayNTric Private Limited'],
              ['Address', '07, Golden Nest Appartment, Garhi, Chaukhandi, Noida, Gautam Buddha Nagar, Uttar Pradesh, 201307'],
              ['Email', 'support@jcentrix.com'],
              // ['Helpline', '+91 9407157175'],
            ].map(([label, value]) => (
              <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.8 }}>
                <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#64748b', minWidth: 90 }}>{label}:</Typography>
                <Typography sx={{ fontSize: '0.83rem', color: '#475569' }}>
                  {label === 'Email' ? (
                    <a href={`mailto:${value}`} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>{value}</a>
                  ) : label === 'Helpline' ? (
                    <a href={`tel:${value.replace(/\s/g, '')}`} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>{value}</a>
                  ) : value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
